import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormField, form, min, required } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';

import { finalize } from 'rxjs';

import { Addon, Agreement, AgreementStatuses, BillingCycles } from '../schemas';
import { AgreementService, Messenger } from '../service';

export interface AddonFormData {
  agreement: Agreement;
}

const AddonNames = [
  'SSL Certificate',
  'Development Hosting',
  'Reservation Costs',
  'Enterprise Account Management',
  'External Software',
  'Additional Storage',
];

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatSelectModule,
    MatIconModule,
    MatDialogModule,
    MatProgressBarModule,
    MatDatepickerModule,
    FormField,
  ],
  template: `
    <h2 mat-dialog-title>Add addon</h2>

    <mat-dialog-content>
      <div class="entity-form">
        <mat-form-field>
          <mat-label> Addon Name </mat-label>
          <mat-select [formField]="form.AddonName">
            @for (name of addonNames(); track name) {
              <mat-option [value]="name">{{ name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Enter a date range</mat-label>
          <mat-date-range-input [rangePicker]="rangePicker">
            <input matStartDate placeholder="Start date" [formField]="form.StartDate" />
            <input matEndDate placeholder="End date" [formField]="form.EndDate" />
          </mat-date-range-input>
          <mat-hint>DD/MM/YYYY – DD/MM/YYYY</mat-hint>
          <mat-datepicker-toggle matIconSuffix [for]="rangePicker"></mat-datepicker-toggle>
          <mat-date-range-picker #rangePicker></mat-date-range-picker>
        </mat-form-field>

        <mat-form-field>
          <mat-label> Billing Cycle </mat-label>
          <mat-select [formField]="form.BillingCycle">
            @for (name of billingCycles(); track name) {
              <mat-option [value]="name">{{ name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label> Status </mat-label>
          <mat-select [formField]="form.Status">
            @for (name of statuses(); track name) {
              <mat-option [value]="name">{{ name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label> Invoice Amount </mat-label>
          <input matInput type="number" [formField]="form.InvoiceAmount" step="0.01" />
        </mat-form-field>

        <mat-checkbox [formField]="form.InvoiceSeparately"> Invoice Separately </mat-checkbox>

        <mat-form-field>
          <mat-label> Next Invoice </mat-label>
          <input matInput [matDatepicker]="picker" [formField]="form.NextInvoiceDate" />
          <mat-hint>DD/MM/YYYY</mat-hint>
          <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button matMiniFab matTooltip="Save product details" (click)="save()" [disabled]="!form().valid() || pending()">
        <mat-icon>save</mat-icon>
      </button>
    </mat-dialog-actions>
    @if (pending()) {
      <mat-progress-bar mode="indeterminate" />
    }
  `,
})
export class AddonForm {
  data = inject(MAT_DIALOG_DATA) as AddonFormData;

  pending = signal(false);

  hostDialog = inject(MatDialogRef);

  messenger = inject(Messenger);

  addonNames = signal(AddonNames);

  billingCycles = signal(BillingCycles);

  statuses = signal(AgreementStatuses);

  agreements = inject(AgreementService);

  model = signal({
    AddonName: 'SSL Certificate',
    StartDate: '',
    EndDate: '',
    BillingCycle: 'Monthly',
    Status: 'Active',
    InvoiceAmount: 0,
    InvoiceSeparately: false,
    NextInvoiceDate: '',
    Id: '',
  } as Addon);

  form = form(this.model, (schema) => {
    required(schema.AddonName);
    required(schema.StartDate);
    required(schema.EndDate);
    required(schema.BillingCycle);
    required(schema.Status);
    required(schema.InvoiceAmount);

    min(schema.InvoiceAmount, 0);
  });

  save() {
    const data = this.form().value();

    this.pending.set(true);

    this.agreements
      .addAddon(this.data.agreement.AgreementNumber, data)
      .pipe(finalize(() => this.pending.set(false)))
      .subscribe({
        next: () => this.hostDialog.close(true),
        error: this.messenger.handleHttpError,
      });
  }
}
